import {
  Component, ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { LogService } from '../../shared/log.service';
import { ISlide, ISlideObject, Slide, SlidePlaylist } from '../../shared/slide';
import { SlideStoreService } from '../../shared/slide-store.service';
import { ImageCacheService } from '../../shared/image-cache-service.service';
import { environment } from '../../../environments/environment';
import { constants } from '../../core/_constant/constants';
import { Subscription } from 'rxjs';

@Component({
    selector: 'lse-slide-image',
    template: `
    <div
      #slideContainer
      class='slide_container'
      [class.fullscreen-controls-visible]="isFullscreen && fullscreenControlsVisible"
      [style.--bg-color]="dominantColor"
      (mousemove)="showFullscreenControls()"
    >
      @defer (on viewport) {
        <img #slideId class='ui image' [ngClass]="{'loaded': imageLoaded}" [src]="imageUrl"
             (load)="onImageLoad($event)" />
        <div class='slide_bar'>
          <div class='fullscreen_button'>
            <i (click)='toggleFullscreen()' [class]="isFullscreen ? 'compress icon' : 'expand icon'"></i>
          </div>
        </div>
        @if (isFullscreen && slidePlaylist.length > 1) {
          <div class='fullscreen-controls'>
            <button
              type='button'
              class='fullscreen-navigation fullscreen-navigation-previous'
              aria-label='Vorherige Folie'
              [disabled]='selectedSlideIndex <= 0'
              [tabindex]='fullscreenControlsVisible ? 0 : -1'
              (click)='selectRelativeSlide(-1)'
            >
              <span aria-hidden='true'>&#10094;</span>
            </button>

            <div class='fullscreen-thumbnails' role='list' aria-label='Folienauswahl'>
              @for (slide of slidePlaylist; track slide._id) {
                <button
                  type='button'
                  class='fullscreen-thumbnail'
                  [class.active]='slide._id === selectedSlideObject?._id'
                  [attr.aria-label]="'Folie ' + (slidePlaylist.indexOf(slide) + 1)"
                  [attr.aria-current]='slide._id === selectedSlideObject?._id ? "true" : null'
                  [tabindex]='fullscreenControlsVisible ? 0 : -1'
                  (click)='selectSlide(slide)'
                >
                  <img [src]='thumbnailUrl(slide)' alt='' />
                </button>
              }
            </div>

            <button
              type='button'
              class='fullscreen-navigation fullscreen-navigation-next'
              aria-label='Nächste Folie'
              [disabled]='selectedSlideIndex >= slidePlaylist.length - 1'
              [tabindex]='fullscreenControlsVisible ? 0 : -1'
              (click)='selectRelativeSlide(1)'
            >
              <span aria-hidden='true'>&#10095;</span>
            </button>
          </div>
        }
      } @placeholder (minimum 1ms) {
        <img class='ui image' src='assets/images/placeholder/placeholder_400.webp' />
      } @error {
        Could not load image
      }
    </div>
  `,
    styles: `
    .slide_bar {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      height: 30px;
      font-size: 18px;
      color: white;
      padding-right: 15px;
      margin-top: -30px;
      position: absolute;
      bottom: 0; /* Position at bottom */
      left: 0;
      width: 100%;
      transition: 1s ease;
      opacity: 0;
      background-color: rgba(43, 51, 63, 0.7);
      z-index: 2;
    }

    .fullscreen_button {
      cursor: pointer;
    }

    .slide_container {
      position: relative;
      aspect-ratio: 16 / 9;
      overflow: hidden;
      background-color: transparent;
      border: 1px solid rgba(218, 218, 218);
      box-sizing: border-box;
    }

    .slide_container::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--bg-color);
      z-index: 0;
      z-index: 0;
    }


    .ui.image {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: contain;
      z-index: 1; /* Ensures image is above the blurred background */
      /*opacity: 0; /* Start with transparent image */
      transition: opacity 0.8s ease-in; /* Slow fade-in transition */
    }

    .ui.image.loaded {
      opacity: 1; /* Fully visible when loaded */
    }

    .ui.image {
      top: 0;
      left: 0;
      width: 100%;
      object-fit: contain; /* Maintains aspect ratio without cropping */
      aspect-ratio: 16/9 !important;
    }


    .slide_container:hover .slide_bar {
      opacity: 1;
    }

    .slide_container:fullscreen .slide_bar {
      visibility: hidden;
      pointer-events: none;
      opacity: 0;
    }

    .slide_container:fullscreen.fullscreen-controls-visible .slide_bar {
      visibility: visible;
      pointer-events: auto;
      opacity: 1;
    }

    .fullscreen-controls {
      position: absolute;
      inset: 0;
      z-index: 3;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 250ms ease, visibility 0s linear 250ms;
    }

    .fullscreen-controls-visible .fullscreen-controls {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transition-delay: 0s;
    }

    .fullscreen-navigation {
      position: absolute;
      top: 50%;
      width: 52px;
      height: 72px;
      transform: translateY(-50%);
      border: 0;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.55);
      color: #fff;
      cursor: pointer;
      font-size: 38px;
      line-height: 1;
    }

    .fullscreen-navigation:hover:not(:disabled),
    .fullscreen-thumbnail:hover {
      background-color: rgba(0, 0, 0, 0.8);
    }

    .fullscreen-navigation:disabled {
      opacity: 0.35;
      cursor: default;
    }

    .fullscreen-navigation-previous {
      left: 24px;
    }

    .fullscreen-navigation-next {
      right: 24px;
    }

    .fullscreen-thumbnails {
      position: absolute;
      right: 90px;
      bottom: 24px;
      left: 90px;
      display: flex;
      justify-content: center;
      gap: 10px;
      max-height: 104px;
      overflow-x: auto;
      padding: 8px;
      background-color: rgba(0, 0, 0, 0.55);
      border-radius: 4px;
    }

    .fullscreen-thumbnail {
      flex: 0 0 128px;
      width: 128px;
      height: 72px;
      padding: 0;
      overflow: hidden;
      border: 2px solid transparent;
      background: rgba(0, 0, 0, 0.55);
      cursor: pointer;
    }

    .fullscreen-thumbnail.active {
      border-color: #fff;
    }

    .fullscreen-thumbnail img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    @media only screen and (max-width: 767px) {
      .fullscreen-navigation {
        width: 40px;
        height: 56px;
        font-size: 30px;
      }

      .fullscreen-navigation-previous {
        left: 12px;
      }

      .fullscreen-navigation-next {
        right: 12px;
      }

      .fullscreen-thumbnails {
        right: 60px;
        bottom: 12px;
        left: 60px;
      }

      .fullscreen-thumbnail {
        flex-basis: 96px;
        width: 96px;
        height: 54px;
      }
    }

    @media only screen and (max-width: 1023px) {
      .slide_bar {
        display: none;
      }
    }`,
    standalone: false
})
export class SlideImageComponent implements OnInit, OnChanges, OnDestroy{
  @Input() eventFolder: string;
  @Input() languages: string[];
  @Input() selectedLanguage: string;

  @ViewChild('slideContainer') slideFullscreen: ElementRef;
  isFullscreen = false;
  fullscreenControlsVisible = false;

  protected readonly constant = constants;
  protected readonly environment = environment;
  private subscriptions: Subscription[] = [];
  slideStoreService: SlideStoreService = inject(SlideStoreService);
  imageCacheService: ImageCacheService = inject(ImageCacheService);
  imageLoaded = false;
  dominantColor: string = 'rgba(240, 240, 240, 0.9)';
  logger: LogService = inject(LogService);

  slidePlaylist: SlidePlaylist[] = [];
  selectedSlideObject: SlidePlaylist;
  private _backgroundImageUrl: string = '';
  private fullscreenControlsTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    console.log(this.selectedLanguage)
    this.slideStoreService.getSlidePlaylist();
    this.subscriptions.push(
      this.slideStoreService.playlist$.subscribe((playlist: SlidePlaylist[]) => {
        this.slidePlaylist = [...playlist];
      }),
      this.slideStoreService.selectedSlide$.subscribe((slide: SlidePlaylist) => {
        this.selectedSlideObject = slide;
        this.imageLoaded = false; // Reset when selected slide changes
        this.updateBackgroundUrl(); // Add this line to update the background URL when slide changes
      })
    );
    this.slideStoreService.initializeSocketConnection();
    document.addEventListener('fullscreenchange', this.onFullscreenChange);;
    this.updateBackgroundUrl();
    this.logger.log('Init Slide: ');
  }
  onFullscreenChange = () => {
    this.isFullscreen = !!document.fullscreenElement;
    if (!this.isFullscreen) {
      this.hideFullscreenControls();
    }
  }

  get selectedSlideIndex(): number {
    return this.slidePlaylist.findIndex((slide) => slide._id === this.selectedSlideObject?._id);
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.openFullscreen();
    }
  }

  exitFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document['webkitExitFullscreen']) {
      document['webkitExitFullscreen']();
    }
  }

  openFullscreen() {
    const elem = this.slideFullscreen.nativeElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    }
  }

  showFullscreenControls(): void {
    if (!this.isFullscreen) {
      return;
    }

    this.fullscreenControlsVisible = true;
    if (this.fullscreenControlsTimer) {
      clearTimeout(this.fullscreenControlsTimer);
    }
    this.fullscreenControlsTimer = setTimeout(() => {
      this.fullscreenControlsVisible = false;
      this.fullscreenControlsTimer = undefined;
    }, 3000);
  }

  selectRelativeSlide(offset: number): void {
    const targetIndex = this.selectedSlideIndex + offset;
    if (targetIndex < 0 || targetIndex >= this.slidePlaylist.length) {
      return;
    }

    this.selectSlide(this.slidePlaylist[targetIndex]);
  }

  selectSlide(slide: SlidePlaylist): void {
    this.slideStoreService.setSelectedSlide(slide);
    this.showFullscreenControls();
  }

  thumbnailUrl(slide: SlidePlaylist): string {
    const source = this.getSlideSource(slide);
    return source
      ? `${this.environment.apiUrl}/event/${this.eventFolder}/${this.constant.SLIDES_FOLDER_PATH}/${source}_200.webp`
      : '';
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.dominantColor = this.imageCacheService.extractAndCacheDominantColor(img, this.imageUrl);

    setTimeout(() => {
      this.imageLoaded = true;
    }, 75);
  }
  get selectedSlideObjectSource(): null | string {
    return this.getSlideSource(this.selectedSlideObject);
  }

  private getSlideSource(slide: SlidePlaylist): null | string {
    if (!slide?.slide) {
      return null;
    }

    // First try: Check selected language
    if (slide.slide[this.selectedLanguage]) {
      return slide.slide[this.selectedLanguage];
    }

    // Second try: Check first language from languages array
    if (this.languages?.length > 0 && slide.slide[this.languages[0]]) {
      return slide.slide[this.languages[0]];
    }

    // Third try: Check second language from languages array
    if (this.languages?.length > 1 && slide.slide[this.languages[1]]) {
      return slide.slide[this.languages[1]];
    }

    // A slide can belong to a language which is no longer configured on the
    // event. Use its available source rather than showing an empty image.
    return Object.values(slide.slide)[0] ?? null;
  }

  get imageUrl(): string {
    if (!this.selectedSlideObjectSource) return '';
    const url = `${this.environment.apiUrl}/event/${this.eventFolder}/${this.constant.SLIDES_FOLDER_PATH}/${this.selectedSlideObjectSource}_2000.webp`;
    // Preload the high-res image
    this.imageCacheService.preloadImage(url);
    return url;
  }

  // get backgroundImageUrl(): string {
  //   return this._backgroundImageUrl;
  // }

  updateBackgroundUrl(): void {
    if (!this.selectedSlideObjectSource) {
      this._backgroundImageUrl = '';
      return;
    }
    this._backgroundImageUrl = `${this.environment.apiUrl}/event/${this.eventFolder}/${this.constant.SLIDES_FOLDER_PATH}/${this.selectedSlideObjectSource}_200.webp`;
    this.imageCacheService.preloadImage(this._backgroundImageUrl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.slidePlaylist) {
      this.slideStoreService.setSelectedSlide(changes.slidePlaylist.currentValue[0]);
    }
    this.updateBackgroundUrl();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.slideStoreService.socket.disconnect();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    this.hideFullscreenControls();
  }

  private hideFullscreenControls(): void {
    this.fullscreenControlsVisible = false;
    if (this.fullscreenControlsTimer) {
      clearTimeout(this.fullscreenControlsTimer);
      this.fullscreenControlsTimer = undefined;
    }
  }

  protected readonly HTMLImageElement = HTMLImageElement;
}
